import { Box, Collapse, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridApiPremium, GridExceljsProcessInput } from '@mui/x-data-grid-premium';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { now, vyDate, withDefault } from '@vy/utils';
import type { Workbook } from 'exceljs';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useFiltersParam } from '../../../../hooks/search-params/use-filters-param';
import { useCustomerRevenueReport } from '../../../../hooks/requests/use-customer-revenue-report';
import { useGridColumnSettings } from '../../../../hooks/use-grid-column-settings';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { toastFromPromise } from '../../../../utils/vy-toasts';
import { formatDate } from '../../../../utils/date';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { buildCustomerRevenueExport } from '../../reports-page/reports-page-utils/customer-revenue-report-utils/build-customer-revenue-export';
import { ReportTable } from '../../reports-components/ReportTable';
import { useCustomerRevenueFilterFields } from '../hooks/use-customer-revenue-filter-fields';
import { applyCustomerRevenueFilters } from '../utils/apply-customer-revenue-filters';
import { applyCustomerRevenueSearch } from '../utils/apply-customer-revenue-search';
import { CustomerRevenueReportChart } from './CustomerRevenueReportChart/CustomerRevenueReportChart';
import type { ChartPeriodMode } from './CustomerRevenueReportChart/use-customer-revenue-report-chart-data';
import { CustomerRevenueReportTable } from './CustomerRevenueReportTable';
import { CUSTOMER_REVENUE_REPORT_KEYS } from './CustomerRevenueReportTable/CustomerRevenueReportTableColumns';
import { CustomerRevenueReportToolbar } from './CustomerRevenueReportToolbar';

const FILTERS_PARAM_NAME = 'customerRevenueFilters';

const COLUMN_PICKER_COLUMNS: GridColDef[] = [
  { field: CUSTOMER_REVENUE_REPORT_KEYS.COUNTRY, headerName: 'Country' },
  { field: CUSTOMER_REVENUE_REPORT_KEYS.STATE, headerName: 'State' },
];

export const CustomerRevenueReportContent: React.FC = () => {
  const { data: customerRevenueReport, isLoading } = useCustomerRevenueReport();
  const [searchText, setSearchText] = useSearchWithSearchParams();

  const defaultFromDate = useMemo(
    () => vyDate().subtract(11, 'month').startOf('month').toDate(),
    [],
  );
  const defaultRecognizedUpTo = useMemo(() => vyDate().endOf('month').toDate(), []);

  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [recognizedUpTo, setRecognizedUpTo] = useState<Date | null>(defaultRecognizedUpTo);
  const [showChart, setShowChart] = useState(false);
  const [chartPeriodMode, setChartPeriodMode] = useState<ChartPeriodMode>('monthly');

  const reportItems = customerRevenueReport.reportItems;
  const filterFields = useCustomerRevenueFilterFields(reportItems);
  const [rawFilters, setRawFilters] = useFiltersParam<FilterLineType>(FILTERS_PARAM_NAME, filterFields);
  const filters = useMemo(() => rawFilters ?? [], [rawFilters]);

  const handleFiltersChange = useCallback(
    (nextFilters: FilterLineType[]) => setRawFilters(nextFilters),
    [setRawFilters],
  );

  const filteredReportItems = useMemo(
    () => applyCustomerRevenueFilters(reportItems, filters),
    [reportItems, filters],
  );

  const chartReportItems = useMemo(
    () => applyCustomerRevenueSearch(filteredReportItems, searchText),
    [filteredReportItems, searchText],
  );

  const filteredReport = useMemo(
    () => ({ ...customerRevenueReport, reportItems: filteredReportItems }),
    [customerRevenueReport, filteredReportItems],
  );

  const apiRef = useGridApiRef();

  const {
    isSettingsOpen: isColumnSettingsOpen,
    openSettings: openColumnSettings,
    closeSettings: closeColumnSettings,
    columnSettings,
    handleColumnVisibilityChange,
  } = useGridColumnSettings(apiRef as MutableRefObject<GridApiCommunity>, COLUMN_PICKER_COLUMNS);

  const handleExportToExcel = async () => {
    const api = apiRef.current;
    if (!api) return;

    const fileName = `Customer Revenue Waterfall (issued ${formatDate(now(), 'MMM D, YYYY')})`;

    await toastFromPromise(
      api.exportDataAsExcel({
        fileName,
        exceljsPostProcess: async ({ workbook }: GridExceljsProcessInput) => {
          buildCustomerRevenueExport(workbook as unknown as Workbook, {
            filteredReportItems,
            searchText,
            fromDate,
            recognizedUpTo,
            filters,
            filterFields,
            totals: customerRevenueReport.totals,
          });
        },
      }),
      {
        pending: 'Downloading...',
        success: 'Download complete',
        error: 'Failed to download',
      },
    );
  };

  const creationDate = withDefault(customerRevenueReport.reportGeneratedAt, new Date());

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "20px"
          }}>
          Revenue Waterfall
        </Typography>
      </Box>
      <ReportTable
        title="Revenue Waterfall"
        hideTitle
        exportCsvFileName="Revenue Waterfall Report"
        reportCreationDate={creationDate ?? new Date()}
        reportType={REPORT_TYPES.CUSTOMER_REVENUE}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        onExportCsv={handleExportToExcel}
        hideMenu
      >
        <CustomerRevenueReportToolbar
          onSearchChange={(value) => setSearchText(value)}
          searchValue={searchText ?? ''}
          fromDate={fromDate}
          onFromDateChange={setFromDate}
          recognizedUpTo={recognizedUpTo}
          onRecognizedUpToChange={setRecognizedUpTo}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          showChart={showChart}
          onShowChartChange={setShowChart}
          onExport={handleExportToExcel}
          columnSettings={columnSettings}
          isColumnSettingsOpen={isColumnSettingsOpen}
          onOpenColumnSettings={openColumnSettings}
          onCloseColumnSettings={closeColumnSettings}
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Collapse in={showChart} timeout={350} unmountOnExit sx={{ flexShrink: 0 }}>
            <CustomerRevenueReportChart
              reportItems={chartReportItems}
              fromDate={fromDate}
              toDate={recognizedUpTo}
              periodMode={chartPeriodMode}
              onPeriodModeChange={setChartPeriodMode}
            />
          </Collapse>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <CustomerRevenueReportTable
              isLoading={isLoading}
              filter={searchText ?? undefined}
              fromDate={fromDate}
              recognizedUpTo={recognizedUpTo}
              apiRef={apiRef as MutableRefObject<GridApiPremium>}
              report={filteredReport}
            />
          </Box>
        </Box>
      </ReportTable>
    </>
  );
};
